/**
 * Cloudflare R2 upload service using AWS Signature V4 (no SDK needed).
 * Uses browser's native crypto.subtle for HMAC-SHA256 signing.
 */

const ACCOUNT_ID = import.meta.env.VITE_CF_ACCOUNT_ID || ''
const ACCESS_KEY = import.meta.env.VITE_R2_ACCESS_KEY_ID || ''
const SECRET_KEY = import.meta.env.VITE_R2_SECRET_ACCESS_KEY || ''
const BUCKET = import.meta.env.VITE_R2_BUCKET_NAME || 'model'
const PUBLIC_URL = import.meta.env.VITE_R2_PUBLIC_URL || ''

// In dev: use Vite proxy to avoid CORS. In prod: direct S3 endpoint.
const R2_ENDPOINT = import.meta.env.DEV
  ? '/r2-upload'
  : `https://${ACCOUNT_ID}.r2.cloudflarestorage.com`
const REGION = 'auto'
const SERVICE = 's3'

// --- Crypto helpers ---
async function hmac(key: ArrayBuffer | string, data: string): Promise<ArrayBuffer> {
  const keyData = typeof key === 'string' ? new TextEncoder().encode(key) : key
  const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  return crypto.subtle.sign('HMAC', cryptoKey, new TextEncoder().encode(data))
}

async function sha256hex(data: ArrayBuffer | string): Promise<string> {
  const buf = typeof data === 'string' ? new TextEncoder().encode(data) : data
  const hash = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

function utcNow(): { date: string; datetime: string } {
  const now = new Date()
  const datetime = now.toISOString().replace(/[:\-]|\.\d{3}/g, '').slice(0, 15) + 'Z'
  const date = datetime.slice(0, 8)
  return { date, datetime }
}

// --- Signing key ---
async function getSigningKey(secretKey: string, date: string): Promise<ArrayBuffer> {
  const kDate = await hmac(`AWS4${secretKey}`, date)
  const kRegion = await hmac(kDate, REGION)
  const kService = await hmac(kRegion, SERVICE)
  return hmac(kService, 'aws4_request')
}

// --- Main upload function ---
export async function uploadToR2(
  file: File,
  folder: string = 'uploads'
): Promise<string> {
  const { date, datetime } = utcNow()

  const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_')
  const key = `${folder}/${Date.now()}-${safeName}`

  const url = `${R2_ENDPOINT}/${BUCKET}/${key}`
  const host = `${ACCOUNT_ID}.r2.cloudflarestorage.com`

  // Hash body
  const fileBuffer = await file.arrayBuffer()
  const bodyHash = await sha256hex(fileBuffer)

  // Canonical headers (must be sorted)
  const canonicalHeaders = [
    `content-type:${file.type}`,
    `host:${host}`,
    `x-amz-content-sha256:${bodyHash}`,
    `x-amz-date:${datetime}`,
  ].join('\n') + '\n'

  const signedHeaders = 'content-type;host;x-amz-content-sha256;x-amz-date'

  const canonicalRequest = [
    'PUT',
    `/${BUCKET}/${key}`,
    '',
    canonicalHeaders,
    signedHeaders,
    bodyHash,
  ].join('\n')

  const credentialScope = `${date}/${REGION}/${SERVICE}/aws4_request`
  const hashedCanonical = await sha256hex(canonicalRequest)

  const stringToSign = [
    'AWS4-HMAC-SHA256',
    datetime,
    credentialScope,
    hashedCanonical,
  ].join('\n')

  const signingKey = await getSigningKey(SECRET_KEY, date)
  const signature = toHex(await hmac(signingKey, stringToSign))

  const authorization = [
    `AWS4-HMAC-SHA256 Credential=${ACCESS_KEY}/${credentialScope}`,
    `SignedHeaders=${signedHeaders}`,
    `Signature=${signature}`,
  ].join(', ')

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'x-amz-content-sha256': bodyHash,
      'x-amz-date': datetime,
      Authorization: authorization,
    },
    body: fileBuffer,
  })

  if (!response.ok) {
    const err = await response.text()
    throw new Error(`R2 upload failed (${response.status}): ${err}`)
  }

  // Return public URL
  return `${PUBLIC_URL}/${key}`
}

export { PUBLIC_URL, BUCKET }
